import type {
  InputConfig,
  ValueConfig,
  InputDescription,
  IntegrationDescription,
  OutputReference,
  Team,
  InputConfig_OperatorConfig,
} from "@smartsuite/automation-engine-api";
import {
  TextEditorDescription_EmojiType,
  TextEditorDescription_TextInputType,
  SplitType,
} from "@smartsuite/automation-engine-api";
import { Paragraph, useSelectState } from "@smartsuite/react-ui/lib";
import type {
  Doc,
  EditorState,
  PillPickerEditorCommands,
} from "@smartsuite/smartdoc";
import { PillPickerEditor } from "@smartsuite/smartdoc";
import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import * as R from "remeda";
import { CaptionBlock } from "../../../../../../../../components/CaptionBlock";
import { useFieldMissingValidation } from "../../../../../../../../stores/automation/hooks/field-missing-validation";
import { checkInputValidation } from "../../../../../../../../stores/automation/utils/inputValidation";
import { useRootStore } from "../../../../../../../../stores/hooks";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import type { DynamicSection } from "../../../../../../../../types/line";
import { valueConfigToSmartDoc } from "../../../../../../../../utils/conditions-coverter.hook";
import {
  ActionLabel,
  WHEN_BUTTON_IS_CLICKED_BUTTON_LABEL,
} from "../../../../../../../../utils/constants";
import { FieldMissingError } from "../../../../../../Sections/ErrorSection";
import {
  convertActionsToDynamicSections,
  convertTriggerToDynamicSection,
} from "../../../../utils";
import { StyledContainerWrapper } from "../../../style";
import { TriggerDropdown } from "../../containers/TriggerDropdown";
import { UpdateValueDropdown } from "../../containers/UpdateValueDropdown";
import {
  generalTypes,
  ssTypes,
  stringOnlySSTypes,
  stringOnlyTypes,
} from "./string-output-types";
import {
  getEditorStyle,
  StyledPillPickerEditorWrapper,
  StyledPlaceholder,
} from "./style";
import { toValueConfig } from "./to-value-config";
import { useCurrentOption } from "../../containers/CurrentOption/useCurrentOptions";
import { usePillTooltip } from "../../../../../../../../utils/make-tooltip.hook";
import { useLoopingMenu } from "../../../../hooks/use-looping-menu";
import { useActionMemberDirectory } from "../../../../../../../../hooks/use-member-directory";
import { pickValueConfigString } from "../../../../../../../../utils/inputs";
import { isTruthy } from "remeda";
import { translateIfPresent } from "../../../../../../../../locales/i18n";
import {
  actionSourceBoundary,
  canOfferActionSources,
  resolveEditorLoopScope,
} from "../../../../utils/action-sources";
import type { LoopScope } from "../../../../../../../../stores/automation/looping.store";

interface Props {
  input: InputCandidate;
  onChange: (input: InputCandidate) => void;
  trigger: TriggerCandidate;
  actions: ActionCandidate[];
  action?: ActionCandidate;
  /** Set by ArgumentEditor when `actions` is an explicit, already-scoped list
   * (Conditional Logic branch conditions): offer it whole even though there is
   * no owning `action` to bound it (BUG-21770). */
  actionsArePreScoped?: boolean;
  /** The loop whose Current Item this editor may offer, used verbatim when
   * `actionsArePreScoped` is set. See useLoopingMenu (BUG-21770). */
  loopScope?: LoopScope;
  integrations: IntegrationDescription[];
  description?: InputDescription;
  isMultiple: boolean;
  showValidationError: boolean;
  isError?: boolean;
  onClickTrash?: () => void;
  editorTypeExternal?: TextEditorDescription_TextInputType;
  hideCaptionBlock?: boolean;
  disableReferences?: boolean;
  placeholder?: string;
  disableSplit?: boolean;
}

const inputIdsWithStringOnlyOutputs = new Set(["channel_dynamic"]);
const teamFieldOutputSuffixesToExclude = [".email", ".members"];

/**
 * True when the input holds nothing the editor could render: no value at
 * all, or only empty text.
 */
const isEmptyValue = (value?: ValueConfig): boolean =>
  value === undefined ||
  value.values.every(
    (item) => item.value.oneofKind === "string" && item.value.string === "",
  );

export const StringEditor = observer<Props>(
  ({
    input,
    onChange,
    trigger,
    actions,
    action,
    actionsArePreScoped,
    loopScope,
    integrations,
    description,
    isMultiple,
    showValidationError,
    isError,
    onClickTrash,
    editorTypeExternal,
    hideCaptionBlock,
    disableReferences,
    placeholder,
    disableSplit,
  }) => {
    const store = useRootStore();
    const buttonRef = useRef<HTMLElement>();
    const commandsRef = useRef<PillPickerEditorCommands>();
    const shouldRender = useRef(true);
    const rootLooping = store.automations.candidate.looping;
    const looping = resolveEditorLoopScope(
      action,
      rootLooping,
      loopScope,
      actionsArePreScoped,
    );
    const teams = useActionMemberDirectory(action).teams.filter(
      (team) => team.name !== "Everyone",
    );
    const teamFieldId = description?.inputId ?? input.inputId;
    const isEntireTeamField =
      description?.icon === "team-field" && !teamFieldId.includes(".");
    const excludedTeamFieldOutputSuffixes =
      description?.icon === "team-field"
        ? teamFieldOutputSuffixesToExclude
        : [];
    const selectedTeamIds =
      input.value?.values.flatMap((item) => {
        if (
          item.value.oneofKind === "function" &&
          item.value.function.function.oneofKind === "teamId"
        ) {
          return [item.value.function.function.teamId.teamId];
        }
        return [];
      }) ?? [];
    placeholder =
      input.inputId === WHEN_BUTTON_IS_CLICKED_BUTTON_LABEL
        ? t("automation-form.string-field-button-label-placeholder")
        : placeholder;
    const [isOpen, setIsOpen] = useState(false);
    const [editorType, setEditorType] =
      useState<TextEditorDescription_TextInputType>();
    const [key, setKey] = useState<number>(1);
    const [skipKeyIncr, setSkipKeyIncr] = useState<boolean>(false);
    const [triggerMenu, setTriggerMenu] = useState<DynamicSection | null>(null);
    const [actionMenus, setActionMenus] = useState<DynamicSection[]>([]);
    const [validationError, setValidationError] = useState<string>("");
    const { isInputValidByFieldMissing, getFieldMissingErrorMessage } =
      useFieldMissingValidation();
    const [smartDoc, setSmartDoc] = useState<Doc>({
      type: "doc",
      content: [],
    });
    const [showEmoji, setShowEmoji] = useState(
      TextEditorDescription_EmojiType.NONE,
    );
    const [focus, setFocus] = useSelectState();
    const [actionType, setActionType] = useState<
      | "replace"
      | "clear"
      | "append"
      | "mathAdd"
      | "mathSubtract"
      | "mathMultiply"
      | "mathDivide"
      | undefined
    >(input.operatorConfig?.operator?.oneofKind);
    const [isCleanAction, setIsCleanAction] = useState<boolean>(false);

    const { addCurrentPill } = useCurrentOption({
      fieldType: "text",
      commandsRef: commandsRef.current,
    });

    const { addTooltipToPills, makeTriggerPillTooltip, makeActionPillTooltip } =
      usePillTooltip();

    const { loopingMenu } = useLoopingMenu({
      loopScope,
      actionsArePreScoped,
      trigger,
      actions,
      integrations,
      types: inputIdsWithStringOnlyOutputs.has(input.inputId)
        ? stringOnlyTypes
        : generalTypes,
      ssFieldTypes: inputIdsWithStringOnlyOutputs.has(input.inputId)
        ? stringOnlySSTypes
        : ssTypes,
      action,
    });

    const addPill = (attrs: PillNodeAttrs): void => {
      const pillValue = JSON.parse(attrs.value);

      if (pillValue.target === "trigger" && trigger.triggerReference) {
        const outputReference: OutputReference = {
          source: {
            oneofKind: "triggerReference",
            triggerReference: trigger.triggerReference,
          },
          outputId: pillValue.value,
        };
        commandsRef.current?.addPill({
          ...attrs,
          value: JSON.stringify(outputReference),
          tooltip: makeTriggerPillTooltip(trigger),
        });
      }

      // Gated on the REFERENCED action, never on the editor's own: a
      // Conditional Logic branch condition has no owning action, and requiring
      // one dropped the pill on the floor (BUG-21770).
      if (pillValue.target === "action") {
        const targetAction = actions.find(
          (candidate) => candidate.hash === pillValue.hash,
        );
        if (targetAction?.actionReference !== undefined) {
          const outputReference: OutputReference = {
            source: {
              oneofKind: "actionReference",
              actionReference: targetAction.actionReference,
            },
            outputId: pillValue.value,
          };
          commandsRef.current?.addPill({
            ...attrs,
            value: JSON.stringify(outputReference),
            tooltip: makeActionPillTooltip(targetAction),
          });
        }
      }

      addCurrentPill(attrs);
      setIsOpen(false);
    };

    const addTeamPill = (team: Team): void => {
      commandsRef.current?.addPill({
        icon: "",
        title: team.name,
        value: JSON.stringify({
          target: "team",
          teamId: team.teamId,
        }),
      });
      setIsOpen(false);
    };

    const focusEditor = (): void => {
      setTimeout(() => {
        const wrapper = document.getElementById(editorWrapperId);
        const editor = wrapper?.querySelector(".ProseMirror");
        // @ts-expect-error
        editor?.focus();
        store.inputToFocus = undefined;
      }, 100);
    };

    const isMultipleRows = (): boolean => {
      return [
        TextEditorDescription_TextInputType.TYPE_LONG,
        TextEditorDescription_TextInputType.TYPE_EXTRA_LONG,
      ].includes(editorType ?? 0);
    };

    const editorWrapperId = `${input.inputId}-editor-wrapper`;

    useEffect(() => {
      const overrideValue =
        description?.overrideValueConfig !== undefined
          ? pickValueConfigString(description.overrideValueConfig)
          : undefined;
      if (
        description?.overrideValueConfig !== undefined &&
        isTruthy(overrideValue) &&
        isEmptyValue(input.value)
      ) {
        onChange({
          ...input,
          splitType: undefined, // it skips splitType change
          value: description.overrideValueConfig,
        });
      }
    }, [description?.overrideValueConfig]);

    useEffect(() => {
      // TODO: On Conditions we provide empty descriuptions
      // as we use short editor all the time on donctions
      if (editorTypeExternal !== undefined && editorType === undefined) {
        setEditorType(editorTypeExternal);
      }
      if (description === undefined) {
        setEditorType(TextEditorDescription_TextInputType.TYPE_SHORT);
        setShowEmoji(TextEditorDescription_EmojiType.NONE);
      } else if (description?.editor?.editor.oneofKind === "text") {
        const text = description?.editor?.editor.text;
        setEditorType(text.type);

        if (text?.emoji !== undefined) {
          setShowEmoji(text.emoji);
        }
      }
    }, [description, input.inputId]);

    useEffect(() => {
      setTriggerMenu(
        convertTriggerToDynamicSection(
          trigger,
          integrations,
          inputIdsWithStringOnlyOutputs.has(input.inputId)
            ? stringOnlyTypes
            : generalTypes,
          inputIdsWithStringOnlyOutputs.has(input.inputId)
            ? stringOnlyTypes
            : generalTypes,
          inputIdsWithStringOnlyOutputs.has(input.inputId)
            ? stringOnlySSTypes
            : ssTypes,
          undefined,
          undefined,
          excludedTeamFieldOutputSuffixes,
        ),
      );
      if (canOfferActionSources(action, actionsArePreScoped)) {
        setActionMenus(
          convertActionsToDynamicSections(
            actions,
            actionSourceBoundary(action, actionsArePreScoped),
            integrations,
            inputIdsWithStringOnlyOutputs.has(input.inputId)
              ? stringOnlyTypes
              : generalTypes,
            inputIdsWithStringOnlyOutputs.has(input.inputId)
              ? stringOnlySSTypes
              : ssTypes,
            [],
            looping,
            undefined,
            excludedTeamFieldOutputSuffixes,
          ),
        );
      }
    }, [trigger, action, actions, integrations, looping, actionsArePreScoped]);

    useEffect(() => {
      if (!input.value) {
        onChange({
          ...input,
          value: { values: [] },
        });
      }
    }, [input, onChange]);

    useEffect(() => {
      if (
        description?.editor?.editor.oneofKind === "text" &&
        description.editor.editor.text.default !== ""
      ) {
        if (!input.value || input.value.values.length === 0) {
          onChange({
            ...input,
            value: {
              values: [
                {
                  value: {
                    oneofKind: "string",
                    string: description.editor.editor.text.default,
                  },
                },
              ],
            },
          });
        }
      }
    }, [description]);

    useEffect(() => {
      if (input.value === undefined) {
        const { doc } = valueConfigToSmartDoc(
          { values: [] },
          trigger,
          actions,
          action,
          loopingMenu?.options,
        );

        setSmartDoc(addTooltipToPills(doc, trigger, actions, action));
        setKey((currentKey) => currentKey + 1);
        setSkipKeyIncr(true);
        return;
      }

      // here if input is not empty we need to re-render smartdoc
      // it should happen only once. On first render
      if (
        input.value.values.length === 0 ||
        (input.value.values.length > 0 && !skipKeyIncr)
      ) {
        let initialInput: InputConfig = input;
        if (
          !skipKeyIncr &&
          isEmptyValue(input.value) &&
          description?.editor?.editor.oneofKind === "text" &&
          description.editor.editor.text.default !== ""
        ) {
          initialInput = {
            ...input,
            value: description?.overrideValueConfig ?? {
              values: [
                {
                  value: {
                    oneofKind: "string",
                    string: description.editor.editor.text.default,
                  },
                },
              ],
            },
          };
        }
        const { doc } = valueConfigToSmartDoc(
          initialInput.value ?? input.value,
          trigger,
          actions,
          action,
          loopingMenu?.options,
        );

        setSmartDoc(addTooltipToPills(doc, trigger, actions, action));
        setKey((currentKey) => currentKey + 1);
      }
      setSkipKeyIncr(true);
    }, [input.inputId, input.value?.values, trigger, editorType]);

    useEffect(() => {
      if (showValidationError) {
        if (R.isTruthy(description)) {
          setValidationError(checkInputValidation(description, input));
        }
      } else {
        setValidationError("");
      }
    }, [input, description, showValidationError]);

    useEffect(() => {
      if (store.inputToFocus?.inputId === input.inputId) {
        focusEditor();
      }
    }, []);

    useEffect(() => {
      if (actionType === "clear") {
        setIsCleanAction(true);
      } else {
        setIsCleanAction(false);
      }
    }, [actionType]);

    const changeActionType = (
      type:
        | "replace"
        | "clear"
        | "append"
        | "mathAdd"
        | "mathSubtract"
        | "mathMultiply"
        | "mathDivide"
        | undefined,
    ): void => {
      setActionType(type);
      if (type === undefined) return;
      setIsOpen(false);

      onChange({
        ...input,
        value:
          type === "clear"
            ? {
                values: [],
              }
            : input.value,
        operatorConfig: {
          // @ts-expect-error
          operator: {
            oneofKind: type,
            [type]: {},
          },
        },
      });
    };

    if (editorType === undefined || smartDoc.content.length === 0) return null;

    const renderEditor = (
      renderInput: InputCandidate,
      operatorType:
        | "replace"
        | "clear"
        | "append"
        | "mathAdd"
        | "mathSubtract"
        | "mathMultiply"
        | "mathDivide"
        | undefined,
    ): ReactNode => {
      const updateValue = (value: EditorState): void => {
        const values = toValueConfig(value, trigger, actions);
        // for some reason when user deletes all values in input - input losing its focus.
        if (values.values.length === 0) {
          focusEditor();
        }
        shouldRender.current = true;
        setSkipKeyIncr(true);
        if (operatorType) {
          onChange({
            ...input,
            value: values,
            splitType: undefined, // it skipps splitType change
            createNewValues: undefined, // it skips createNewValues change
            operatorConfig: {
              // @ts-expect-error
              operator: {
                oneofKind: operatorType,
                [operatorType]: {},
              },
            },
          });
        } else {
          onChange({
            ...renderInput,
            splitType: undefined, // it skipps splitType change
            createNewValues: undefined, // it skips createNewValues change
            value: toValueConfig(value, trigger, actions),
          });
        }
        setIsOpen(false);
      };

      // @ts-expect-error
      const newOperator: InputConfig_OperatorConfig =
        actionType !== undefined
          ? {
              operator: {
                oneofKind: actionType,
                [actionType]: {},
              },
            }
          : {
              operator: {
                oneofKind: undefined,
              },
            };

      return (
        // We need to remount component on change operator to clear values
        <div key={operatorType}>
          <PillPickerEditor
            editable={!isCleanAction}
            showAddButton={!isCleanAction}
            key={key}
            state={getEditorState(
              focus,
              isOpen,
              isError ?? !isInputValidByFieldMissing(renderInput, action),
            )}
            isOpen={isOpen}
            onChangeFocused={setFocus}
            className={getEditorStyle(editorType, validationError)}
            initialDoc={smartDoc}
            commandsRef={commandsRef}
            multiline={isMultipleRows()}
            heightMode="grow"
            // @ts-expect-error
            buttonRef={buttonRef}
            onButtonClick={() => setIsOpen(true)}
            onStateChange={updateValue}
            placeholder={
              placeholder === undefined
                ? t("automation-form.string-field-placeholder")
                : translateIfPresent(
                    `automation-form.${placeholder}`,
                    placeholder,
                  )
            }
            onFocus={(e) => {
              description?.editor?.hidePopupOnHover !== true &&
                setTimeout(() => {
                  // @ts-expect-error
                  e.target?.focus();
                });
            }}
            onBlur={() => {
              if (R.isTruthy(disableReferences)) {
                setIsOpen(false);
              }
            }}
          >
            {action?.actionReference?.actionId === ActionLabel.updateRecord &&
              description?.operators?.operators !== undefined &&
              description?.operators?.operators.length > 0 && (
                <UpdateValueDropdown
                  operatorConfig={
                    actionType !== undefined
                      ? newOperator
                      : input.operatorConfig
                  }
                  operatorDescriptions={description?.operators.operators}
                  onOpenedChange={(v) => {
                    setFocus(v);
                  }}
                  onChange={changeActionType}
                />
              )}

            {isCleanAction && (
              <StyledPlaceholder>
                {t("automation-form.clean-operator-editor-placeholder")}
              </StyledPlaceholder>
            )}

            {isOpen && !R.isTruthy(disableReferences) && (
              <TriggerDropdown
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                addPill={addPill}
                triggerMenu={triggerMenu}
                actionMenus={actionMenus}
                loopingMenu={loopingMenu ?? null}
                loopScope={looping}
                actionsArePreScoped={actionsArePreScoped}
                // @ts-expect-error
                buttonRef={buttonRef}
                showEmoji={showEmoji}
                action={action}
                showTeams={isEntireTeamField}
                teams={teams}
                selectedTeamIds={selectedTeamIds}
                onSelectTeam={addTeamPill}
                onChange={(value: string) => {
                  shouldRender.current = true;
                  onChange({
                    ...renderInput,
                    value: {
                      values: [
                        {
                          value: {
                            oneofKind: "string",
                            string: value,
                          },
                        },
                      ],
                    },
                  });
                }}
                onCloseAnimationEnd={() => {}}
              />
            )}
          </PillPickerEditor>
        </div>
      );
    };

    return (
      <StyledContainerWrapper>
        {description !== undefined && !hideCaptionBlock && (
          <CaptionBlock
            description={description}
            onClickTrash={onClickTrash}
            splitType={input.splitType ?? SplitType.NONE}
            onSplitChange={
              isMultiple && !disableSplit
                ? (splitType) => {
                    onChange({
                      ...input,
                      splitType,
                    });
                  }
                : undefined
            }
          />
        )}
        <StyledPillPickerEditorWrapper
          id={editorWrapperId}
          className={disableReferences === true ? "disable-references" : ""}
        >
          {renderEditor(input, actionType)}
          {validationError !== "" && (
            <Paragraph color={"error"} size={"xs"}>
              {validationError}
            </Paragraph>
          )}
          <FieldMissingError
            message={getFieldMissingErrorMessage(input, action)}
          />
        </StyledPillPickerEditorWrapper>
      </StyledContainerWrapper>
    );
  },
);

const getEditorState = (
  focus: string,
  isOpen: boolean,
  isError?: boolean,
): "active" | "default" | "error" => {
  if (isError) {
    return "error";
  } else if (focus || isOpen) {
    return "active";
  } else {
    return "default";
  }
};
